/**
 * OmegaTeaPage (iter 134) — full-feed multi-ticker news page with filters
 * for sentiment, category, and credibility. Sources auto-refresh every 5 min.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import OmegaNewsCard from "./OmegaNewsCard";
import { usePreload } from "./PreloadContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_WATCHLIST = ["SPY", "QQQ", "AAPL", "NVDA", "TSLA", "MSFT"];
const REFRESH_MS = 5 * 60 * 1000;

export default function OmegaTeaPage() {
  const { tea: warmTea } = usePreload();
  const [articles, setArticles] = useState(warmTea || []);
  const [loading, setLoading] = useState(!warmTea);
  const [filterSent, setFilterSent] = useState("ALL");
  const [filterCat, setFilterCat] = useState("ALL");
  const [filterTicker, setFilterTicker] = useState("ALL");
  const [lastRefresh, setLastRefresh] = useState(null);
  const [tickerInput, setTickerInput] = useState("");
  const [pinnedTicker, setPinnedTicker] = useState(null);
  const [pinnedError, setPinnedError] = useState(null);
  // Active-load token — if user pins a ticker mid-fetch, the older watchlist
  // load aborts itself instead of overwriting the pinned results.
  const loadToken = useRef(0);
  const pinnedRef = useRef(null);

  useEffect(() => { pinnedRef.current = pinnedTicker; }, [pinnedTicker]);

  const load = useCallback(async () => {
    const myToken = ++loadToken.current;
    setLoading(true);
    try {
      let tickers = [...DEFAULT_WATCHLIST];
      try {
        const pf = await axios.get(`${API}/portfolio`);
        const syms = (pf.data?.positions || [])
          .map(p => (p.symbol || "").replace(/(\d{6})[CP].*$/, "").replace(/\d/g, ""))
          .filter(Boolean);
        tickers = [...new Set([...syms, ...tickers])].slice(0, 8);
      } catch { /* portfolio failure is fine */ }

      const results = await Promise.all(tickers.map(t =>
        axios.get(`${API}/market/news/v2?symbol=${t}&limit=4&insight=true`)
          .then(r => r.data?.articles || [])
          .catch(() => [])
      ));
      // Abort if a newer load (or a pinned ticker) has taken over
      if (myToken !== loadToken.current || pinnedRef.current) return;
      const flat = [].concat(...results);
      const seen = new Map();
      for (const a of flat) {
        if (!seen.has(a.id)) seen.set(a.id, a);
      }
      const arr = Array.from(seen.values()).sort((a, b) =>
        (b.publishedAt || "").localeCompare(a.publishedAt || "")
      );
      setArticles(arr);
      setLastRefresh(new Date());
    } catch (e) {
      console.warn("Tea load failed", e);
    }
    if (myToken === loadToken.current) setLoading(false);
  }, []);

  const loadPinned = useCallback(async (sym) => {
    const myToken = ++loadToken.current;
    setLoading(true);
    setPinnedError(null);
    try {
      const r = await axios.get(`${API}/market/news/v2?symbol=${sym}&limit=20&insight=true`);
      // Abort if user cleared / pinned a different ticker in the meantime
      if (myToken !== loadToken.current) return;
      const arr = (r.data?.articles || []).sort((a, b) =>
        (b.publishedAt || "").localeCompare(a.publishedAt || "")
      );
      setArticles(arr);
      setLastRefresh(new Date());
      if (arr.length === 0) {
        setPinnedError(`No news found for ${sym}. Double-check the ticker.`);
      }
    } catch (e) {
      if (myToken !== loadToken.current) return;
      setPinnedError(`Couldn't fetch news for ${sym}: ${e.response?.data?.detail || e.message}`);
      setArticles([]);
    } finally {
      if (myToken === loadToken.current) setLoading(false);
    }
  }, []);

  const submitTickerSearch = (e) => {
    e?.preventDefault?.();
    const sym = (tickerInput || "").trim().toUpperCase();
    if (!sym || !/^[A-Z.\-]{1,8}$/.test(sym)) {
      setPinnedError("Enter a valid ticker symbol (e.g. AAL, NVDA, BRK.B).");
      return;
    }
    setPinnedTicker(sym);
    setFilterSent("ALL");
    setFilterCat("ALL");
    setFilterTicker("ALL");
    loadPinned(sym);
  };

  const clearPinned = () => {
    setPinnedTicker(null);
    setTickerInput("");
    setPinnedError(null);
    load();
  };

  useEffect(() => {
    // If we have warm cache from PreloadContext, render instantly + refresh in background
    if (warmTea && warmTea.length && !articles.length) {
      setArticles(warmTea);
      setLoading(false);
    }
  }, [warmTea, articles.length]);

  useEffect(() => {
    if (pinnedTicker) return undefined;  // don't override the user's ticker search
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load, pinnedTicker]);

  // When pinned, also auto-refresh that ticker every 5 min
  useEffect(() => {
    if (!pinnedTicker) return undefined;
    const id = setInterval(() => loadPinned(pinnedTicker), REFRESH_MS);
    return () => clearInterval(id);
  }, [pinnedTicker, loadPinned]);

  const tickerOptions = ["ALL", ...Array.from(new Set(articles.map(a => a.ticker).filter(Boolean)))];

  const filtered = articles.filter(a => {
    if (filterSent !== "ALL" && (a.sentiment || "neutral") !== filterSent) return false;
    if (filterCat !== "ALL" && (a.category || "general") !== filterCat) return false;
    if (filterTicker !== "ALL" && a.ticker !== filterTicker) return false;
    return true;
  });

  return (
    <div data-testid="omega-tea-page" style={{
      minHeight: "100vh", background: "var(--bg, #0a0a14)",
      color: "var(--text, #e6e6f5)", padding: "20px 16px 60px",
    }}>
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        <header style={{ marginBottom: 18 }}>
          <h1 style={{ fontSize: 32, fontWeight: 900, letterSpacing: -0.8, margin: 0 }}>
            Omega <span style={{ color: "#a78bfa" }}>Tea</span>
          </h1>
          <p style={{ fontSize: 13, color: "var(--muted, #aaa)", margin: "4px 0 0" }}>
            Multi-source headlines, cross-referenced, with Omega's AI breakdown.
            {lastRefresh && <span> · Refreshed {lastRefresh.toLocaleTimeString()}</span>}
          </p>
        </header>

        {/* iter 142 — Custom ticker search (pick any symbol, e.g. AAL for American Airlines) */}
        <form onSubmit={submitTickerSearch} style={{
          display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center",
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "6px 10px 6px 14px", borderRadius: 999,
            background: "var(--layer1, rgba(255,255,255,0.04))",
            border: "1px solid var(--border, rgba(255,255,255,0.10))",
            flex: "1 1 240px", maxWidth: 360,
          }}>
            <span style={{ color: "var(--muted, #aaa)", fontSize: 13, fontWeight: 700 }}>🔍</span>
            <input
              data-testid="tea-ticker-search"
              value={tickerInput}
              onChange={e => setTickerInput(e.target.value.toUpperCase())}
              onKeyDown={e => { if (e.key === "Enter") submitTickerSearch(e); }}
              placeholder="Pick a ticker — e.g. AAL, NVDA, BRK.B"
              maxLength={8}
              style={{
                flex: 1, background: "none", border: "none", outline: "none",
                color: "var(--text)", fontSize: 13, fontWeight: 600,
                letterSpacing: 1, fontFamily: "inherit",
              }}
            />
            {tickerInput && (
              <button
                type="button"
                onClick={() => setTickerInput("")}
                aria-label="clear input"
                style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", padding: 0, fontSize: 14 }}
              >×</button>
            )}
          </div>
          <button
            type="submit"
            data-testid="tea-ticker-search-submit"
            disabled={loading || !tickerInput.trim()}
            style={{
              padding: "8px 16px", borderRadius: 999,
              background: tickerInput.trim() ? "#a78bfa" : "var(--layer2, rgba(255,255,255,0.04))",
              color: tickerInput.trim() ? "#fff" : "var(--muted)",
              border: "none", fontSize: 12, fontWeight: 800, letterSpacing: 0.3,
              cursor: tickerInput.trim() && !loading ? "pointer" : "default",
              fontFamily: "inherit",
            }}
          >Search news</button>
          {pinnedTicker && (
            <button
              type="button"
              onClick={clearPinned}
              data-testid="tea-pinned-clear"
              style={{
                padding: "7px 14px", borderRadius: 999,
                background: "rgba(167,139,250,0.15)",
                border: "1px solid rgba(167,139,250,0.4)",
                color: "#a78bfa", fontSize: 11, fontWeight: 800, letterSpacing: 0.4,
                cursor: "pointer", fontFamily: "inherit",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}
            >☕ Showing {pinnedTicker} · clear ✕</button>
          )}
        </form>
        {pinnedError && (
          <div data-testid="tea-pinned-error" style={{
            padding: "8px 12px", marginBottom: 12, borderRadius: 8,
            fontSize: 11.5, color: "#f87171",
            background: "rgba(248,113,113,0.06)",
            border: "1px solid rgba(248,113,113,0.28)",
          }}>{pinnedError}</div>
        )}

        {/* Filters */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          <FilterChip label="Sentiment" value={filterSent} setValue={setFilterSent} options={["ALL", "bullish", "bearish", "neutral"]} testid="filter-sent" />
          <FilterChip label="Category" value={filterCat} setValue={setFilterCat} options={["ALL", "earnings", "analyst", "macro", "insider", "legal", "product", "general"]} testid="filter-cat" />
          <FilterChip label="Ticker" value={filterTicker} setValue={setFilterTicker} options={tickerOptions} testid="filter-ticker" />
          <button
            data-testid="tea-refresh"
            onClick={load}
            disabled={loading}
            style={{
              marginLeft: "auto", padding: "6px 14px", borderRadius: 999,
              background: "rgba(167,139,250,0.18)", border: "1px solid rgba(167,139,250,0.40)",
              color: "#a78bfa", fontSize: 11, fontWeight: 800, cursor: loading ? "wait" : "pointer", letterSpacing: 0.3,
            }}
          >{loading ? "Refreshing…" : "↻ Refresh"}</button>
        </div>

        {/* Feed */}
        {loading && articles.length === 0 && (
          <div style={{ textAlign: "center", padding: 40, color: "var(--muted, #aaa)" }}>Brewing the Tea…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: 40, color: "var(--muted, #aaa)" }}>
            No matches — loosen the filters.
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 14 }}>
          {filtered.map(a => (
            <OmegaNewsCard key={a.id} article={a} />
          ))}
        </div>
      </div>
    </div>
  );
}

function FilterChip({ label, value, setValue, options, testid }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontSize: 10, color: "var(--muted, #aaa)", textTransform: "uppercase", letterSpacing: 1.1, fontWeight: 700 }}>{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={e => setValue(e.target.value)}
        style={{
          padding: "5px 10px", borderRadius: 999,
          background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)",
          color: "var(--text, #e6e6f5)", fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
          cursor: "pointer",
        }}
      >
        {options.map(o => <option key={o} value={o} style={{ background: "#14141f" }}>{o}</option>)}
      </select>
    </div>
  );
}
